import { useState, useCallback } from "react";
import { adminApi } from "../repositories/admin";

interface WeekData {
  week_key: string;
  start_date: string;
  end_date: string;
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
}

interface TrackingData {
  id: number;
  api_call: string;
  date: string;
  time: string;
  userId?: number;
  labelId?: number;
  email?: string;
  labelName?: string;
  isSucceeded: boolean;
  createdAt: string;
}

interface SummaryData {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  successRate: string;
}

interface PaginationData {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface TrackingResponse {
  success: boolean;
  weeks: WeekData[];
  summary: SummaryData;
  pagination?: PaginationData;
  data?: TrackingData[];
}

export const useSoundChartTracking = () => {
  const [analytics, setAnalytics] = useState<TrackingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(
    async (
      months: number = 3,
      includeData: boolean = false,
      page: number = 1,
      limit: number = 100
    ) => {
      try {
        setLoading(true);
        setError(null);

        const response = await adminApi.getAllSoundChartTracking({
          months,
          includeData,
          page,
          limit,
        });

        setAnalytics(response.data);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch SoundChart analytics";
        setError(errorMessage);
        console.error("Error fetching SoundChart analytics:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const refetch = useCallback(
    (months?: number, includeData?: boolean, page?: number, limit?: number) => {
      return fetchAnalytics(months, includeData, page, limit);
    },
    [fetchAnalytics]
  );

  return {
    analytics,
    loading,
    error,
    fetchAnalytics,
    refetch,
  };
};
